import { type ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Permission, roleHasPermissions, type Domain, type DomainStatus } from '@lizrd/contracts';
import {
  getSubscriptionUsage,
  listDomainAssessments,
  listDomains,
  setDomainStatus,
} from '../lib/api';
import { useAuthStore } from '../store/auth';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert } from '../components/ui/alert';
import { Menu } from '../components/ui/menu';
import { SkeletonRows } from '../components/ui/skeleton';
import { UsageMeter } from '../components/UsageMeter';
import { UtilizationScore } from '../components/UtilizationScore';
import { usd } from '../lib/graph-visuals';
import { AddDomainModal } from './domains/AddDomainModal';

/** A compact labelled figure shown in a domain row. */
function MiniStat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="min-w-[72px] text-center">
      <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-0.5 flex items-center justify-center gap-1 text-sm text-bone">
        {value}
        {hint && <span className="text-[0.55rem] text-muted">({hint})</span>}
      </div>
    </div>
  );
}

const STATUS_VARIANT: Record<DomainStatus, 'success' | 'warning' | 'neutral'> = {
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  ARCHIVED: 'neutral',
};

export function DomainsPage() {
  const role = useAuthStore((s) => s.user?.role);
  const canManage = role ? roleHasPermissions(role, [Permission.DomainsManage]) : false;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // The guided add/finish-setup flow — `resume` re-enters at Connect for a domain
  // that was created but never set up.
  const [addOpen, setAddOpen] = useState(false);
  const [resume, setResume] = useState<Domain | null>(null);

  const domains = useQuery({ queryKey: ['domains'], queryFn: listDomains });
  const usage = useQuery({ queryKey: ['subscription'], queryFn: getSubscriptionUsage });
  const assessments = useQuery({
    queryKey: ['domain-assessments'],
    queryFn: listDomainAssessments,
    // Poll so a sync that finishes while this page is open updates the scores
    // without a manual reload (also caught by sync-completion invalidation).
    refetchInterval: 60_000,
  });
  const assessmentById = new Map((assessments.data ?? []).map((a) => [a.domainId, a]));

  const domainUsage = usage.data?.domains;
  const atLimit = domainUsage ? domainUsage.used >= domainUsage.max : false;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['domains'] });
    qc.invalidateQueries({ queryKey: ['subscription'] });
    qc.invalidateQueries({ queryKey: ['domain-assessments'] });
  };

  const changeStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: DomainStatus }) =>
      setDomainStatus(id, status),
    onSuccess: invalidate,
    onError: (e) => setError(e instanceof Error ? e.message : 'Failed to update status'),
  });

  const openAdd = () => {
    setResume(null);
    setAddOpen(true);
  };
  const openFinish = (env: Domain) => {
    setResume(env);
    setAddOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Domains</h1>
          <p className="max-w-prose text-sm text-muted">
            A domain is a slice of your world you manage as one — a whole product line, a single
            product, or one team&rsquo;s architecture. You choose the granularity; most teams start
            with one and add more as they grow (up to 10).
          </p>
        </div>
        <div className="flex flex-none flex-col items-end gap-3">
          {usage.data && (
            <UsageMeter
              label="domains"
              used={usage.data.domains.used}
              max={usage.data.domains.max}
            />
          )}
          {canManage && (
            <Button
              type="button"
              onClick={openAdd}
              disabled={atLimit}
              title={atLimit ? 'Domain limit reached' : undefined}
            >
              <Plus className="h-4 w-4" aria-hidden />
              Add a domain
            </Button>
          )}
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {canManage && atLimit && (
        <Alert variant="warning">
          You&rsquo;ve reached the {domainUsage?.max}-domain limit.{' '}
          <a className="font-medium text-iris hover:underline" href="/help">
            Talk to us
          </a>{' '}
          to lift it.
        </Alert>
      )}

      {domains.isLoading ? (
        <SkeletonRows rows={4} rightBlocks={3} />
      ) : (
        <Card className="divide-y divide-hairline">
          {domains.data?.length === 0 && (
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <p className="text-sm text-muted">No domains yet.</p>
              {canManage && (
                <Button type="button" onClick={openAdd}>
                  <Plus className="h-4 w-4" aria-hidden />
                  Add your first domain
                </Button>
              )}
            </div>
          )}
          {domains.data?.map((env) => {
            const a = assessmentById.get(env.id);
            // A domain with no assessment has never synced — surface a way to finish
            // setting it up (connect + first sync) instead of landing on an empty page.
            const needsSetup = canManage && !a && !assessments.isLoading;
            // Lead with the real billed month-to-date cost when the ledger has it,
            // falling back to our estimate — the same preference the domain
            // dashboard uses, so the list and the dashboard always tie out.
            const hasBilled = a != null && a.billedMonthlyCost != null && a.billedMonthlyCost > 0;
            const monthlyCost = hasBilled ? a.billedMonthlyCost! : a?.totalMonthlyCost;
            return (
              <div key={env.id}>
                <div
                  onClick={() => navigate(`/domains/${env.id}`)}
                  className="flex cursor-pointer flex-wrap items-center gap-x-6 gap-y-3 p-4 transition-colors hover:bg-surface-2/40"
                >
                  {/* Identity */}
                  <div className="min-w-[180px] flex-1">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/domains/${env.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-bone transition-colors hover:text-iris"
                      >
                        {env.name}
                      </Link>
                      <Badge variant={STATUS_VARIANT[env.status]}>{env.status.toLowerCase()}</Badge>
                    </div>
                    <p className="mt-0.5 text-xs text-muted">{env.slug}</p>
                    {needsSetup && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openFinish(env);
                        }}
                        className="mt-1 text-xs font-semibold text-iris transition-colors hover:text-iris-strong"
                      >
                        Finish setup →
                      </button>
                    )}
                  </div>

                  {/* At-a-glance figures */}
                  <div className="flex items-center gap-6">
                    <MiniStat
                      label="Monthly cost"
                      value={a ? usd(monthlyCost!) : '—'}
                      hint={
                        a && !hasBilled
                          ? 'est.'
                          : a && hasBilled && a.unbilledClouds.length > 0
                            ? `${a.unbilledClouds.length} missing billing`
                            : undefined
                      }
                    />
                    <MiniStat
                      label="Utilization"
                      value={<UtilizationScore score={a?.utilizationScore ?? null} />}
                    />
                    <MiniStat label="Optimizations" value={a ? a.costOptimizationActions : '—'} />
                  </div>

                  {/* Management */}
                  {canManage && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Menu
                        items={[
                          ...(env.status !== 'ACTIVE'
                            ? [
                                {
                                  label: 'Activate',
                                  onClick: () =>
                                    changeStatus.mutate({ id: env.id, status: 'ACTIVE' }),
                                },
                              ]
                            : []),
                          ...(env.status === 'ACTIVE'
                            ? [
                                {
                                  label: 'Suspend',
                                  onClick: () =>
                                    changeStatus.mutate({ id: env.id, status: 'SUSPENDED' }),
                                },
                              ]
                            : []),
                          ...(env.status !== 'ARCHIVED'
                            ? [
                                {
                                  label: 'Archive',
                                  onClick: () =>
                                    changeStatus.mutate({ id: env.id, status: 'ARCHIVED' }),
                                  danger: true,
                                },
                              ]
                            : []),
                          {
                            label: 'Manage access',
                            onClick: () => navigate(`/domains/${env.id}/settings#manage-access`),
                          },
                        ]}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </Card>
      )}

      <AddDomainModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        resumeDomain={resume}
        onChanged={invalidate}
      />
    </div>
  );
}
